/**
 * Maps a route's first path segment to the canonical sidebar `selectedItem` id.
 * Extracted from the layout components so the alias mapping (which drives
 * sidebar highlight only) is unit-testable without mounting the provider stack.
 */

export const NO_ID_SELECTED = "NO_ID_SELECTED";

const firstSegment = (pathname: string): string =>
  pathname.split("/")[1] || NO_ID_SELECTED;

const secondSegment = (pathname: string): string =>
  pathname.split("/")[2] || "";

const fourthSegment = (pathname: string): string =>
  pathname.split("/")[4] || "";

/** Organisation-level layout: small alias set. */
export const resolveSelectedItem = (pathname: string): string => {
  switch (firstSegment(pathname)) {
    case "employee-setup":
      return "employees";
    case "project-setup":
      return "list-projects";
    // Org Insights routes are /organisation-insights/<section>; the sidebar
    // child ids are `insights-<section>` (e.g. insights-labour-hours).
    case "organisation-insights":
      return `insights-${secondSegment(pathname)}`;
    // Compliance is one sidebar child per framework family, so the highlight
    // discriminates on the family — the same shape the project sidebar's
    // `analytics-compliance-<frameworkId>` uses.
    case "organisation-compliance":
      return `org-compliance-${secondSegment(pathname)}`;
    default:
      return firstSegment(pathname);
  }
};

/** Project dashboard layout: route family → sidebar group. */
export const resolveProjectSelectedItem = (pathname: string): string => {
  switch (firstSegment(pathname)) {
    case "view-project-report":
    case "view-monthly-report":
      return "project-reports";
    case "package-details":
    case "package-setup":
    case "view-package-report":
      return "packages";
    case "monthly-report-submission":
    case "project-employee-setup-submission":
      return "monthly-reports";
    case "analytics-labour-hours-employee":
      return "analytics-labour-hours";
    // Compliance is one sidebar child per framework, so the highlight has to
    // discriminate on the framework too — the route is
    // /analytics-compliance/<projectId>/<packageId>/<frameworkId> and the child
    // ids are `analytics-compliance-<frameworkId>`. Same shape as the org
    // sidebar's `insights-<section>`.
    case "analytics-compliance":
      return `analytics-compliance-${fourthSegment(pathname)}`;
    case "project-employee-setup":
      return "project-employees";
    case "create-export":
      return "export-generator";
    default:
      return firstSegment(pathname);
  }
};
